// src/docsApi.ts
import { getAxios, setApiBaseUrl } from "@coremind/identity-core";
var resolveApiBaseUrl = () => {
  if (typeof window !== "undefined") {
    return window.__COREMIND_API_URL__ ?? window.__API_URL__ ?? null;
  }
  return null;
};
var axiosInstance = () => {
  try {
    return getAxios();
  } catch (err) {
    const fallback = resolveApiBaseUrl();
    if (fallback) {
      setApiBaseUrl(fallback);
      return getAxios();
    }
    throw err;
  }
};
var docsApiBaseUrl = null;
var resolveBase = (config) => docsApiBaseUrl ?? config?.baseURL ?? axiosInstance().defaults.baseURL ?? "(none)";
var logApiError = (method, url, error, config) => {
  const err = error;
  const status = err?.response?.status;
  const data = err?.response?.data;
  console.warn(
    `[docs-core] ${method} ${url} failed`,
    {
      baseURL: resolveBase(config),
      status,
      data,
      message: err?.message
    }
  );
};
var setDocsApiBaseUrl = (url) => {
  docsApiBaseUrl = url ? url.replace(/\/$/, "") : null;
};
var withBase = (config) => docsApiBaseUrl ? { ...config ?? {}, baseURL: docsApiBaseUrl } : config;
var apiGet = async (url, config) => {
  console.info("[docs-core] GET", url, { baseURL: resolveBase(config), params: config?.params });
  try {
    const { data } = await axiosInstance().get(url, withBase(config));
    return data;
  } catch (error) {
    logApiError("GET", url, error, config);
    throw error;
  }
};
var apiPost = async (url, body, config) => {
  try {
    const { data } = await axiosInstance().post(url, body, withBase(config));
    return data;
  } catch (error) {
    logApiError("POST", url, error, config);
    throw error;
  }
};
var apiPut = async (url, body, config) => {
  try {
    const { data } = await axiosInstance().put(url, body, withBase(config));
    return data;
  } catch (error) {
    logApiError("PUT", url, error, config);
    throw error;
  }
};
var apiDelete = async (url, config) => axiosInstance().delete(url, withBase(config)).catch((error) => {
  logApiError("DELETE", url, error, config);
  throw error;
});
var listDocuments = async (params) => apiGet("/content/documents", { params });
var getDocument = async (documentId) => apiGet(`/content/documents/${documentId}`);
var createDocument = async (payload) => apiPost("/content/documents", payload);
var updateDocument = async (documentId, payload) => apiPut(`/content/documents/${documentId}`, payload);
var listContentTypes = async (params) => apiGet("/content/types", { params });
var listSectionExecutions = async (sectionId) => apiGet(`/content/sections/${sectionId}/executions`);
var deleteDocument = async (documentId) => {
  await apiDelete(`/content/documents/${documentId}`);
};
var addSectionToDocument = async (documentId, payload) => {
  return apiPost(`/content/documents/${documentId}/sections`, payload);
};
var updateDocumentSection = async (documentId, documentSectionId, payload) => {
  return apiPut(`/content/documents/${documentId}/sections/${documentSectionId}`, payload);
};
var removeDocumentSection = async (documentId, documentSectionId) => {
  await apiDelete(`/content/documents/${documentId}/sections/${documentSectionId}`);
};
var listSections = async (params) => apiGet("/content/sections", { params });
var getSection = async (sectionId) => apiGet(`/content/sections/${sectionId}`);
var createSection = async (payload) => {
  return apiPost(`/content/sections`, payload);
};
var updateSection = async (sectionId, payload) => {
  return apiPut(`/content/sections/${sectionId}`, payload);
};
var deleteSection = async (sectionId) => {
  await apiDelete(`/content/sections/${sectionId}`);
};
var importDocumentPackage = async (payload) => {
  return apiPost("/content/documents/import", payload);
};
var listSnippets = async (params) => apiGet("/content/snippets", { params });
var getSnippet = async (snippetId) => apiGet(`/content/snippets/${snippetId}`);
var createSnippet = async (payload) => {
  return apiPost(`/content/snippets`, payload);
};
var updateSnippet = async (snippetId, payload) => {
  return apiPut(`/content/snippets/${snippetId}`, payload);
};
var deleteSnippet = async (snippetId) => {
  await apiDelete(`/content/snippets/${snippetId}`);
};
var executeSnippet = async (snippetId, payload) => {
  return apiPost(`/content/snippets/${snippetId}/execute`, payload);
};
var listSnippetExecutions = async (snippetId) => apiGet(`/content/snippets/${snippetId}/executions`);
var addSnippetToSection = async (sectionId, payload) => {
  return apiPost(`/content/sections/${sectionId}/snippets`, payload);
};
var updateSectionSnippet = async (sectionId, sectionSnippetId, payload) => {
  return apiPut(`/content/sections/${sectionId}/snippets/${sectionSnippetId}`, payload);
};
var removeSectionSnippet = async (sectionId, sectionSnippetId) => {
  await apiDelete(`/content/sections/${sectionId}/snippets/${sectionSnippetId}`);
};
var reorderDocumentSections = async (documentId, sections) => {
  return apiPut(
    `/content/documents/${documentId}/sections/order`,
    { sections }
  );
};
var listResourceShares = async (params) => {
  return apiGet("/content/shares", { params });
};
var createResourceShare = async (payload) => {
  return apiPost("/content/shares", payload);
};
var updateResourceShare = async (resourceShareId, payload) => {
  return apiPut(`/content/shares/${resourceShareId}`, payload);
};
var deleteResourceShare = async (resourceShareId) => {
  await apiDelete(`/content/shares/${resourceShareId}`);
};
var listDocsTasks = async (params) => apiGet("/content/tasks", { params });
var listPlatformWorkflows = async (params) => apiGet("/platform/workflows", { params });
var listCommandContracts = async (params) => apiGet("/command/contracts", { params });
var listPlatformDocsBlueprints = async (params) => apiGet("/platform/docs/blueprints", { params });
var previewPlatformDocsBlueprintImport = async (payload) => apiPost(
  "/platform/docs/blueprints/import",
  {
    ...payload,
    dry_run: true
  }
);
export {
  addSectionToDocument,
  addSnippetToSection,
  createDocument,
  createResourceShare,
  createSection,
  createSnippet,
  deleteDocument,
  deleteResourceShare,
  deleteSection,
  deleteSnippet,
  executeSnippet,
  getDocument,
  getSection,
  getSnippet,
  importDocumentPackage,
  listCommandContracts,
  listContentTypes,
  listDocsTasks,
  listDocuments,
  listPlatformDocsBlueprints,
  listPlatformWorkflows,
  listResourceShares,
  listSectionExecutions,
  listSections,
  listSnippetExecutions,
  listSnippets,
  previewPlatformDocsBlueprintImport,
  removeDocumentSection,
  removeSectionSnippet,
  reorderDocumentSections,
  setDocsApiBaseUrl,
  updateDocument,
  updateDocumentSection,
  updateResourceShare,
  updateSection,
  updateSectionSnippet,
  updateSnippet
};
